import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faCopy,
  faEnvelope,
  faGlobe,
  faHandshake,
  faShareNodes,
  faTag,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState, RootState, Toaster } from '@share/utils';
import { createWebsite } from '@store/slices';
import { validateDomain } from '@utils';

import styles from './styles.module.scss';

const BENEFITS: { icon: IconDefinition; titleId: string; bodyId: string }[] = [
  { icon: faTag, titleId: 'my.account.hub.website.benefit_rates_title', bodyId: 'my.account.hub.website.benefit_rates_body' },
  { icon: faShareNodes, titleId: 'my.account.hub.website.benefit_share_title', bodyId: 'my.account.hub.website.benefit_share_body' },
  { icon: faHandshake, titleId: 'my.account.hub.website.benefit_clients_title', bodyId: 'my.account.hub.website.benefit_clients_body' },
];

// The create API reports failures as `... reason: "<message>"}`; show only the message (as My Dashboard did).
const getApiReason = (error: string): string => (error.split('reason')[1] || error).replace(/[:}"]/g, '').trim();

/**
 * The agent's personal booking site, in the hub's style. Same data and action as My Dashboard's
 * "My Website" (`user.webSite`, `user.webSiteIsActive`, `createWebsite`, `validateDomain`, the
 * `VITE_LEVARTI_REDIRECT` URL); only the layout is new.
 */
export const AccountWebsite = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const user = useSelector((state: FunctionalRootState) => state.loginStore.user);
  const { loading, isWebSiteCreated, webSite, error } = useSelector(
    (state: FunctionalRootState) => state.accountUserSaveStore,
  );

  // A site saved on the profile, or one created in this session (the profile updates on next load).
  const siteName = user?.webSite || (isWebSiteCreated ? webSite : '');
  const hasSite = !!siteName;
  const isLive = !!user?.webSiteIsActive;

  const [name, setName] = useState<string>(webSite || '');

  const baseUrl = import.meta.env.VITE_LEVARTI_REDIRECT || '';
  const urlFor = (value: string) => `${baseUrl}${value}/search`;
  const displayBase = baseUrl.replace(/^https?:\/\//, '');

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Same rule as before: only letters, numbers, dots, hyphens and underscores, lower-cased.
    if (!value || validateDomain(value)) {
      setName(value.toLowerCase());
    }
  };

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    if (name && validateDomain(name) && !loading) {
      dispatch(createWebsite(name));
    }
  };

  const siteUrl = urlFor(siteName);

  const handleCopy = () => {
    if (!navigator.clipboard) {
      Toaster.error(intl.formatMessage({ id: 'my.account.hub.website.copy_failed' }));
      return;
    }
    navigator.clipboard.writeText(siteUrl)
      .then(() => Toaster.success(intl.formatMessage({ id: 'sidekick.rewards.clipboard.message' })))
      .catch((copyError: unknown) => {
        console.error('Account hub: copying the website link failed', copyError);
        Toaster.error(intl.formatMessage({ id: 'my.account.hub.website.copy_failed' }));
      });
  };

  const mailtoHref = `mailto:?subject=${encodeURIComponent(intl.formatMessage({ id: 'my.account.hub.website.email_subject' }))}`
    + `&body=${encodeURIComponent(intl.formatMessage({ id: 'my.account.hub.website.email_body' }, { url: siteUrl }))}`;

  const hero = (
    <section className={styles.siteHero} aria-labelledby="account-hub-website-title">
      <span className={styles.siteHeroIcon} aria-hidden="true"><FontAwesomeIcon icon={faGlobe} /></span>
      <div>
        <h2 id="account-hub-website-title" className={styles.siteHeroTitle}>
          {intl.formatMessage({ id: hasSite ? 'my.account.hub.website.title_live' : 'my.account.hub.website.title_new' })}
        </h2>
        <p className={styles.siteHeroBody}>
          {intl.formatMessage({ id: hasSite ? 'my.account.hub.website.body_live' : 'my.account.hub.website.body_new' })}
        </p>
      </div>
    </section>
  );

  if (hasSite) {
    return (
      <div className={styles.agentTool}>
        {hero}

        <section className={styles.siteCard} aria-labelledby="account-hub-website-link">
          <div className={styles.siteCardHeader}>
            <h3 id="account-hub-website-link" className={styles.siteCardTitle}>
              {intl.formatMessage({ id: 'my.account.hub.website.your_link' })}
            </h3>
            <span className={isLive ? `${styles.siteStatus} ${styles.siteStatusLive}` : `${styles.siteStatus} ${styles.siteStatusPending}`}>
              <FontAwesomeIcon icon={isLive ? faCircleCheck : faClock} aria-hidden="true" />
              {intl.formatMessage({ id: isLive ? 'my.account.hub.website.status_live' : 'my.account.hub.website.status_pending' })}
            </span>
          </div>

          <div className={styles.siteUrl}>
            <span className={styles.siteUrlText}>{siteUrl.replace(/^https?:\/\//, '')}</span>
            <button type="button" className={styles.iconButton} onClick={handleCopy} aria-label={intl.formatMessage({ id: 'my.account.hub.website.copy' })}>
              <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
            </button>
          </div>

          {!isLive ? (
            <p className={styles.siteNote}>{intl.formatMessage({ id: 'my.account.hub.website.pending_note' })}</p>
          ) : null}

          <div className={styles.siteActions}>
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" className={styles.primaryButton}>
              {intl.formatMessage({ id: 'my.account.hub.website.open' })}
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
            </a>
            <button type="button" className={styles.secondaryButton} onClick={handleCopy}>
              <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
              {intl.formatMessage({ id: 'my.account.hub.website.copy' })}
            </button>
            <a href={mailtoHref} className={styles.secondaryButton}>
              <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
              {intl.formatMessage({ id: 'my.account.hub.website.email' })}
            </a>
          </div>
        </section>

        <p className={styles.siteHelp}>{intl.formatMessage({ id: 'my.account.hub.website.help' })}</p>
      </div>
    );
  }

  const isValid = !!name && validateDomain(name);
  const apiError = error && !isWebSiteCreated ? getApiReason(error) : '';

  return (
    <div className={styles.agentTool}>
      {hero}

      <ul className={styles.benefits}>
        {BENEFITS.map(benefit => (
          <li key={benefit.titleId} className={styles.benefit}>
            <span className={styles.benefitIcon} aria-hidden="true"><FontAwesomeIcon icon={benefit.icon} /></span>
            <strong>{intl.formatMessage({ id: benefit.titleId })}</strong>
            <span>{intl.formatMessage({ id: benefit.bodyId })}</span>
          </li>
        ))}
      </ul>

      <form className={styles.siteCard} onSubmit={handleCreate} noValidate>
        <label htmlFor="account-hub-website-name" className={styles.siteCardTitle}>
          {intl.formatMessage({ id: 'my.account.hub.website.choose_name' })}
        </label>
        <p className={styles.siteNote}>{intl.formatMessage({ id: 'my.account.hub.website.name_rules' })}</p>

        <div className={styles.urlBuilder}>
          <span className={styles.urlAffix} aria-hidden="true">{displayBase}</span>
          <input
            id="account-hub-website-name"
            className={styles.urlInput}
            value={name}
            onChange={handleNameChange}
            disabled={loading}
            placeholder={intl.formatMessage({ id: 'my.account.hub.website.name_placeholder' })}
            autoComplete="off"
            spellCheck={false}
            aria-describedby="account-hub-website-preview"
            aria-invalid={!!apiError}
          />
          <span className={styles.urlAffix} aria-hidden="true">/search</span>
        </div>

        <p id="account-hub-website-preview" className={styles.urlPreview}>
          {name
            ? intl.formatMessage({ id: 'my.account.hub.website.preview' }, { url: urlFor(name).replace(/^https?:\/\//, '') })
            : intl.formatMessage({ id: 'my.account.hub.website.preview_empty' })}
        </p>

        {apiError ? (
          <p className={styles.siteError} role="alert">
            <FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" />
            {apiError}
          </p>
        ) : null}

        <div className={styles.siteActions}>
          <button type="submit" className={styles.primaryButton} disabled={!isValid || loading}>
            {intl.formatMessage({ id: loading ? 'my.account.hub.website.creating' : 'my.account.hub.website.create' })}
          </button>
        </div>
      </form>

      <p className={styles.siteHelp}>{intl.formatMessage({ id: 'my.account.hub.website.help' })}</p>
    </div>
  );
};
